import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import swal from 'sweetalert';
import { getMyGroupTours, markCompleteGroupTour } from '../../api/groupToursApi';
import VendorOrdersCalendar from './VendorOrdersCalendar';
import { VendorPayoutLegendVendor } from '../admin/AdminStatusLegend';
import { vendorPayoutStatus, vendorTripMonthKey, formatVendorShare } from '../../utils/vendorPayoutStatus';
import '../admin/BookingCalendar.scss';

export default function PartnerGroupToursList() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchTours = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getMyGroupTours({ per_page: 500, sort_by: 'date', sort_order: 'desc' });
      const raw = res.data?.group_tours || res.data || [];
      setTours(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.warn('PartnerGroupToursList fetch error', e);
      setError(true);
      setTours([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const handleMarkComplete = (t) => {
    if (!t.idx) return;
    setMarkingId(t.idx);
    markCompleteGroupTour(t.idx)
      .then(() => { swal('Done', 'Marked complete. Admin will record your payment.', 'success'); fetchTours(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
      .finally(() => setMarkingId(null));
  };

  return (
    <div className="container py-4">
      <Link to="/partner" className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
      <h2 className="mb-3">Group Trips</h2>
      <VendorPayoutLegendVendor />
      <p className="text-muted small mb-3">Click a month → mark complete when the trip is done.</p>

      {error && (
        <div className="alert alert-warning">
          Could not load trips. <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchTours}>Retry</button>
        </div>
      )}

      {!error && (
        <VendorOrdersCalendar
          items={tours}
          getMonthKey={vendorTripMonthKey}
          loading={loading}
          emptyMessage="No group trips assigned yet."
          renderTable={(monthItems) => (
            <div className="table-responsive">
              <table className="table table-sm table-hover booking-cal__table">
                <thead>
                  <tr>
                    <th>Trip</th>
                    <th>Date</th>
                    <th>People</th>
                    <th>Your amount</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {monthItems.map((t) => {
                    const status = vendorPayoutStatus({ ...t, vendor_amount: t.vendor_amount ?? t.vendor_payout }, { audience: 'vendor' });
                    return (
                      <tr key={t.idx || t.id}>
                        <td><strong>{t.name || '—'}</strong></td>
                        <td>{t.start_date ? moment(t.start_date).format('D MMM YYYY') : '—'}</td>
                        <td>{t.paid_people_count ?? '—'} paid · min {t.min_group_size ?? '—'}</td>
                        <td>{formatVendorShare(t)}</td>
                        <td>
                          <span className={`badge badge-${status.badge}`}>{status.label}</span>
                          {status.hint && <div className="small text-muted">{status.hint}</div>}
                        </td>
                        <td className="text-right">
                          {!t.vendor_completed_at && (
                            <button type="button" className="btn btn-sm btn-success" onClick={() => handleMarkComplete(t)} disabled={markingId === t.idx}>
                              {markingId === t.idx ? '…' : 'Mark complete'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        />
      )}
    </div>
  );
}
